import { useQuery } from '@apollo/client';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { SettingsCard } from '../../core/components/SettingsFormCard.tsx';
import Stats from '../../core/components/Stats.tsx';
import { GET_CUSTOMER_PLAN_USAGE } from '../queries.ts';
import SupportOnlyBadge from './SupportOnlyBadge.tsx';

interface IPlanUsage {
    planName: string;
    quota: number | null;
    usedQuota: number;
    seatLimit: number | null;
    usedSeats: number;
    chatAgentsEnabled: boolean;
    voiceAgentsEnabled: boolean;
    monthlyLlmTokenLimit: number | null;
    // Decimal fields are serialized as strings
    monthlyChatLlmCostLimit: string | null;
    usedMonthlyChatLlmCost: string;
    monthlyVoiceCallLimit: number | null;
    usedMonthlyVoiceCalls: number;
    monthlyVoiceCallMinutesLimit: number | null;
    usedMonthlyVoiceCallMinutes: number;
}

// null and -1 both mean "no limit"
const hasLimit = (limit: number | null): limit is number => limit !== null && limit >= 0;

const parseDecimal = (value: string | null): number | null => (value === null ? null : parseFloat(value));

const PlanUsageValue = ({ children }: { children: React.ReactNode }) => {
    return <div className="text-sm font-medium text-content-strong">{children}</div>;
};

interface UsageRowProps {
    label: string;
    used: number;
    limit: number | null;
    formatValue?: (value: number) => string;
}

const UsageRow = ({ label, used, limit, formatValue }: UsageRowProps) => {
    const { i18n } = useTranslation();
    const limited = hasLimit(limit);
    const format = formatValue ?? ((value: number) => value.toLocaleString(i18n.language));
    return (
        <div className="flex flex-col gap-2">
            <Stats.Row>
                <Stats.Label>{label}</Stats.Label>
                <PlanUsageValue>
                    {format(used)} / {limited ? format(limit) : '∞'}
                </PlanUsageValue>
            </Stats.Row>
            {limited && limit > 0 && (
                <Stats.Bar>
                    <Stats.BarSection count={Math.min(used, limit)} totalCount={limit} className="bg-data-data1" />
                </Stats.Bar>
            )}
        </div>
    );
};

const PlanUsageCard = () => {
    const { t, i18n } = useTranslation('customer');

    const { data } = useQuery(GET_CUSTOMER_PLAN_USAGE);
    const planUsage: IPlanUsage | null | undefined = data?.viewer?.customer?.planUsage;

    if (!planUsage) return null;

    return (
        <SettingsCard title={t('planUsage.title')} description={t('planUsage.description')} className="relative">
            <SupportOnlyBadge />
            <Stats>
                <Stats.Group>
                    <Stats.Row>
                        <Stats.Label>{t('planUsage.plan')}</Stats.Label>
                        <PlanUsageValue>{planUsage.planName}</PlanUsageValue>
                    </Stats.Row>
                    <Stats.Row>
                        <Stats.Label>{t('planUsage.chatAgents')}</Stats.Label>
                        <PlanUsageValue>
                            {planUsage.chatAgentsEnabled ? t('planUsage.enabled') : t('planUsage.disabled')}
                        </PlanUsageValue>
                    </Stats.Row>
                    <Stats.Row>
                        <Stats.Label>{t('planUsage.voiceAgents')}</Stats.Label>
                        <PlanUsageValue>
                            {planUsage.voiceAgentsEnabled ? t('planUsage.enabled') : t('planUsage.disabled')}
                        </PlanUsageValue>
                    </Stats.Row>
                    <Stats.Row>
                        <Stats.Label>{t('planUsage.llmTokens')}</Stats.Label>
                        <PlanUsageValue>
                            {hasLimit(planUsage.monthlyLlmTokenLimit)
                                ? planUsage.monthlyLlmTokenLimit.toLocaleString(i18n.language)
                                : '∞'}
                        </PlanUsageValue>
                    </Stats.Row>
                </Stats.Group>

                <Stats.Group>
                    <UsageRow label={t('planUsage.documents')} used={planUsage.usedQuota} limit={planUsage.quota} />
                    <Stats.Divider />
                    <UsageRow label={t('planUsage.seats')} used={planUsage.usedSeats} limit={planUsage.seatLimit} />
                    <Stats.Divider />
                    <UsageRow
                        label={t('planUsage.chatLlmCost')}
                        used={parseDecimal(planUsage.usedMonthlyChatLlmCost) ?? 0}
                        limit={parseDecimal(planUsage.monthlyChatLlmCostLimit)}
                        formatValue={(value) =>
                            value.toLocaleString(i18n.language, { style: 'currency', currency: 'USD' })
                        }
                    />
                    <Stats.Divider />
                    <UsageRow
                        label={t('planUsage.voiceCalls')}
                        used={planUsage.usedMonthlyVoiceCalls}
                        limit={planUsage.monthlyVoiceCallLimit}
                    />
                    <Stats.Divider />
                    <UsageRow
                        label={t('planUsage.voiceCallMinutes')}
                        used={planUsage.usedMonthlyVoiceCallMinutes}
                        limit={planUsage.monthlyVoiceCallMinutesLimit}
                    />
                </Stats.Group>
            </Stats>
        </SettingsCard>
    );
};

export default PlanUsageCard;
